import { type PropsWithChildren, useRef } from 'react';

import { SseEngineContext } from './SseEngineContextValue';
import { type SSEHandler, type SSEMessageTypes } from '../types';
import { useSse } from '../useSse';

export const SseEngineContextProvider = ({ children }: PropsWithChildren) => {
  const sseHandlers = useRef<Partial<Record<SSEMessageTypes, SSEHandler[]>>>(
    {},
  );
  const initialHandlers = useRef<Set<() => void>>(new Set());

  const addSseHandler = (key: SSEMessageTypes, handler: SSEHandler) => {
    sseHandlers.current = {
      ...sseHandlers.current,
      [key]: Array.isArray(sseHandlers.current[key])
        ? [...sseHandlers.current[key], handler]
        : [handler],
    };
  };

  const removeSseHandler = (key: SSEMessageTypes, handler: SSEHandler) => {
    sseHandlers.current = {
      ...sseHandlers.current,
      [key]:
        Array.isArray(sseHandlers.current[key]) &&
        sseHandlers.current[key]?.includes(handler)
          ? sseHandlers.current[key]?.filter((el) => el !== handler)
          : sseHandlers.current[key],
    };
  };

  const addInitialHandler = (handler: () => void) => {
    initialHandlers.current.add(handler);
  };

  const removeInitialHandler = (handler: () => void) => {
    initialHandlers.current.delete(handler);
  };

  useSse(sseHandlers, initialHandlers);

  return (
    <SseEngineContext.Provider
      value={{
        removeSseHandler,
        addSseHandler,
        addInitialHandler,
        removeInitialHandler,
      }}
    >
      {children}
    </SseEngineContext.Provider>
  );
};
